// Formula:
// rate = apr / 12 (divide by 10000 as apr stored e.g. 12.9% as 1290 => 0.129)
// c = compounding interest = (1 + rate) ** terms
// monthly = principal * rate * c / (c - 1)

import { FinanceRate } from '@kanda-libs/ks-frontend-services';
import { pipe } from 'fp-ts/lib/function';
import {
  extractPriorityRateByLender,
  groupByDuration,
  GroupedByKey,
} from 'utils';
import { FinanceFormType } from '../../context';
import { CalculationsValues } from './useCalculations';

export const calculateMonthlyPayment = (
  apr: number,
  terms: number,
  amount: number,
): number => {
  if (apr === 0) return Math.ceil(amount / terms);
  const monthlyInterest = apr / 12 / 10000; // r
  const compoundInterest = (1 + monthlyInterest) ** terms;
  const monthlyLoan =
    (amount * (monthlyInterest * compoundInterest)) / (compoundInterest - 1);
  return Math.ceil(monthlyLoan);
};

export const calculateBorrowAmount = (
  apr: number,
  terms: number,
  monthly: number,
): number => {
  if (apr === 0) return Math.ceil(monthly * terms);
  const monthlyInterest = apr / 12 / 10000;
  const compoundInterest = (1 + monthlyInterest) ** terms;
  const principal =
    (monthly * (compoundInterest - 1)) / (monthlyInterest * compoundInterest);
  return Math.ceil(principal);
};

export const filterForAprMatch = (
  rates: FinanceRate[],
  apr: string,
): FinanceRate[] => rates.filter((rate) => rate.apr === parseInt(apr, 10));

export const calculateMonthlyRepaymentValue = (
  apr: number,
  terms: number,
  amount: number,
  rate: FinanceRate,
): CalculationsValues => {
  const monthlyRepayment = calculateMonthlyPayment(apr, terms, amount);
  const {
    min_job_value: minJobValue = 0,
    max_job_value: maxJobValue = Infinity,
  } = rate;
  const valid = amount >= minJobValue && amount <= maxJobValue;
  return {
    term: String(terms),
    amount: monthlyRepayment,
    valid,
    rate,
    total: amount,
  };
};

export const calculateBorrowAmountValue = (
  apr: number,
  terms: number,
  amount: number,
  rate: FinanceRate,
): CalculationsValues => {
  const borrowAmount = calculateBorrowAmount(apr, terms, amount);
  const {
    min_job_value: minJobValue = 0,
    max_job_value: maxJobValue = Infinity,
  } = rate;
  const valid = borrowAmount >= minJobValue && borrowAmount <= maxJobValue;
  return {
    term: String(terms),
    amount: borrowAmount,
    valid,
    rate,
    total: amount,
  };
};

export const calculateValuesForMatchedRates = (
  rates: FinanceRate[],
  type: FinanceFormType,
  amount: number,
): CalculationsValues[] =>
  rates.map((rate: FinanceRate) => {
    const { apr, duration } = rate;
    if (apr === undefined || duration === undefined)
      return {
        term: '0',
        amount: 0,
        valid: true,
        rate,
        total: 0,
      };
    if (amount === 0)
      return {
        term: String(duration),
        amount,
        valid: true,
        rate,
        total: 0,
      };
    if (type === 'monthlyPayments')
      return calculateMonthlyRepaymentValue(apr, duration, amount, rate);
    return calculateBorrowAmountValue(apr, duration, amount, rate);
  });

export const extractGroupPriorityFinance = (
  grouped: GroupedByKey,
): FinanceRate[] =>
  Object.keys(grouped).reduce(
    (final: FinanceRate[], current: string) => [
      ...final,
      extractPriorityRateByLender(grouped[current]),
    ],
    [],
  );

export const filterUniqueDurations = (rates: FinanceRate[]): FinanceRate[] =>
  pipe(rates, groupByDuration, extractGroupPriorityFinance);
